import { useCallback, useEffect, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import { appliquerMode, ecrireMode, lireMode, type ModeApparence } from './apparence-console';
import {
  appliquerFiligrane,
  ecrireFiligrane,
  lireFiligrane,
  NIVEAUX_FILIGRANE,
  type NiveauFiligrane,
} from './filigrane-console';

/**
 * Le sélecteur d'apparence de la console — trois choix, dans le pied du rail.
 *
 * TROIS CHOIX ET PAS DEUX. Un simple interrupteur clair/sombre force le tuteur
 * à choisir un camp ; « Automatique » est le défaut, et c'est lui qui rend la
 * console cohérente avec le reste de la machine le soir venu. Les mêmes trois
 * options, dans le même ordre, que l'écran « Apparence » du mobile — un
 * professionnel qui utilise les deux surfaces ne doit pas réapprendre.
 *
 * Rendu comme un groupe de boutons radio, et pas comme trois boutons : c'est un
 * choix parmi trois, et un lecteur d'écran doit l'entendre comme tel (« 2 sur
 * 3 »). L'état actif n'est pas signalé que par la couleur — il porte
 * `aria-checked` et une pastille de surface (`--piste-neutre-actif`, avec son
 * contour en mode sombre).
 *
 * CLAVIER : tabulation ROULANTE, comme le veut le motif radiogroup. Le groupe
 * ne prend qu'UN arrêt de tabulation — seule l'option cochée est atteignable —
 * et les flèches circulent entre les options en les sélectionnant au passage.
 * Trois arrêts de tabulation pour un choix unique feraient de ce petit réglage
 * le tiers du parcours clavier du pied de rail.
 */
const OPTIONS: readonly { readonly mode: ModeApparence; readonly libelle: string }[] = [
  { mode: 'clair', libelle: 'Clair' },
  { mode: 'sombre', libelle: 'Sombre' },
  { mode: 'systeme', libelle: 'Auto' },
];

/** Option suivante/précédente, en boucle — le comportement attendu des flèches. */
function voisine(mode: ModeApparence, pas: 1 | -1): ModeApparence {
  const i = OPTIONS.findIndex((o) => o.mode === mode);
  const j = (i + pas + OPTIONS.length) % OPTIONS.length;
  return OPTIONS[j]!.mode;
}

export function ApparenceToggle() {
  // L'état initial vient du stockage, pas d'un défaut : sans ça, le premier
  // rendu afficherait « Auto » sélectionné sur une console réglée en sombre.
  const [mode, setMode] = useState<ModeApparence>(() => lireMode());

  // Le mode a déjà été posé sur `<html>` par `initialiserApparence()` avant le
  // premier rendu ; cet effet ne sert qu'à re-synchroniser si un autre onglet
  // a changé le réglage.
  useEffect(() => {
    appliquerMode(mode);
  }, [mode]);

  const choisir = useCallback((suivant: ModeApparence) => {
    ecrireMode(suivant);
    setMode(suivant);
    appliquerMode(suivant);
  }, []);

  const auClavier = useCallback(
    (evenement: ReactKeyboardEvent<HTMLDivElement>) => {
      const pas =
        evenement.key === 'ArrowRight' || evenement.key === 'ArrowDown'
          ? 1
          : evenement.key === 'ArrowLeft' || evenement.key === 'ArrowUp'
            ? -1
            : 0;
      if (pas === 0) return;
      evenement.preventDefault();
      const suivant = voisine(mode, pas);
      choisir(suivant);
      // Le focus suit la sélection : sans ça, les flèches suivantes repartiraient
      // du bouton d'origine et le parcours deviendrait imprévisible.
      evenement.currentTarget.querySelector<HTMLButtonElement>(`[data-mode="${suivant}"]`)?.focus();
    },
    [mode, choisir],
  );

  return (
    <div
      className="apparence"
      role="radiogroup"
      aria-label="Apparence de la console"
      onKeyDown={auClavier}
    >
      {OPTIONS.map((option) => (
        <button
          key={option.mode}
          type="button"
          role="radio"
          data-mode={option.mode}
          aria-checked={mode === option.mode}
          // Tabulation roulante : un seul arrêt pour tout le groupe.
          tabIndex={mode === option.mode ? 0 : -1}
          className={mode === option.mode ? 'apparence__opt is-on' : 'apparence__opt'}
          onClick={() => choisir(option.mode)}
        >
          {option.libelle}
        </button>
      ))}
    </div>
  );
}

/**
 * LE FILIGRANE — même idiome que l'apparence, juste au-dessous.
 *
 * Le fondateur, 15 septembre 2026 : « même pas moyen de régler l'opacité ou de
 * le changer ». Trois crans, parce que l'opacité est cuite dans le motif (voir
 * `filigrane-console.ts`) : promettre un curseur continu serait mentir.
 *
 * Même motif d'accessibilité que le sélecteur ci-dessus — groupe radio,
 * tabulation roulante, état porté par `aria-checked` et pas par la seule
 * couleur.
 */
const LIBELLE_FILIGRANE: Readonly<Record<NiveauFiligrane, string>> = {
  plein: 'Plein',
  discret: 'Discret',
  aucun: 'Aucun',
};

function voisinFiligrane(niveau: NiveauFiligrane, pas: 1 | -1): NiveauFiligrane {
  const i = NIVEAUX_FILIGRANE.indexOf(niveau);
  const j = (i + pas + NIVEAUX_FILIGRANE.length) % NIVEAUX_FILIGRANE.length;
  return NIVEAUX_FILIGRANE[j]!;
}

export function FiligraneToggle() {
  const [niveau, setNiveau] = useState<NiveauFiligrane>(() => lireFiligrane());

  useEffect(() => {
    appliquerFiligrane(niveau);
  }, [niveau]);

  const choisir = useCallback((suivant: NiveauFiligrane) => {
    ecrireFiligrane(suivant);
    setNiveau(suivant);
    appliquerFiligrane(suivant);
  }, []);

  const auClavier = useCallback(
    (evenement: ReactKeyboardEvent<HTMLDivElement>) => {
      const pas =
        evenement.key === 'ArrowRight' || evenement.key === 'ArrowDown'
          ? 1
          : evenement.key === 'ArrowLeft' || evenement.key === 'ArrowUp'
            ? -1
            : 0;
      if (pas === 0) return;
      evenement.preventDefault();
      const suivant = voisinFiligrane(niveau, pas);
      choisir(suivant);
      evenement.currentTarget
        .querySelector<HTMLButtonElement>(`[data-filigrane-opt="${suivant}"]`)
        ?.focus();
    },
    [niveau, choisir],
  );

  return (
    <div
      className="apparence"
      role="radiogroup"
      aria-label="Filigrane de la console"
      onKeyDown={auClavier}
    >
      {NIVEAUX_FILIGRANE.map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          data-filigrane-opt={n}
          aria-checked={niveau === n}
          tabIndex={niveau === n ? 0 : -1}
          className={niveau === n ? 'apparence__opt is-on' : 'apparence__opt'}
          onClick={() => choisir(n)}
        >
          {LIBELLE_FILIGRANE[n]}
        </button>
      ))}
    </div>
  );
}
